import React, { useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { push } from "../../../utils/navigation";
import { addLocationToNewRun } from "../../../actions/runActions";
import { Button, Progress } from "reactstrap";
import moment from "moment";
import { BASE_URL, SHORT_DATE_FORMAT, CUSTOMER_TYPES_NAMES } from "../../../constants";
import { Badge } from "reactstrap";
import {getRunFlag} from "../../../utils/collectionTypes";

/**
 *
 * @param {{
 *  location: import("../SearchLocationsPage").Location,
 *  isSelected: boolean,
 *  regionName: string
 *  onRemoveLocation: (location: import("../SearchLocationsPage").Location) => void
 *  onLoadManagedLocation: (locationId: number) => void
 *  onToggleRunSearch: (evt: any, location: import("../SearchLocationsPage").Location) => void
 *  onOpenRunModal: (evt: any, runId: number, locationId: number) => void 
 * }} props
 * @returns
 */
function LocationItem({
  location,
  isSelected = false,
  regionName = "",
  onRemoveLocation = () => {},
  onLoadManagedLocation = () => {},
  onToggleRunSearch = () => {},
  onOpenRunModal = () => {},  
}) {
  const dispatch = useDispatch();

  const getStyle = useMemo(() => {
    if (
      location.daysSinceLastPickup == undefined ||
      location.daysSinceLastPickup == null
    ) {
      return { border: "solid 1px blue" };
    }
    // Only mark urgent if the urgency type matches the location's customer type context.
    // A Collection-type location is only "urgent" here if it has an urgent collection request;
    // a Fresh Oil (delivery) location is only "urgent" if it has an urgent delivery request.
    const isCollection = location.customerTypeCode === CUSTOMER_TYPES_NAMES.Collection;
    const relevantUrgent = isCollection ? location.isUrgentCollection : location.isUrgentDelivery;
    if (relevantUrgent) {
      return { border: "solid 1px red" };
    }
    return {};
  }, [location]);

  function round(value = 0, decimals = 0) {
    return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
  }

  const isCollectionType = useMemo(() => {
    return location.customerTypeCode === CUSTOMER_TYPES_NAMES.Collection;
  }, [location]);

  const getProgressColor = useCallback(
    (plus7 = false) => {
      let color = "success";
      const { percentageOfCapacity, percentageOfCapacityPlus7 } = location;      
      
      const percentage = plus7
        ? percentageOfCapacityPlus7
        : percentageOfCapacity;
      if (!isCollectionType) {
        if (percentage <= 15) {
          color = "danger";
        } else if (percentage <= 30) {
          color = "warning";
        }
      } else {
        if (percentage >= 85) {
          color = "danger";
        } else if (percentage >= 70) {
          color = "warning";
        }
      }
      
      if(percentage <= 0) {
        color = "danger";
      }
      
      return color;
    },
    [location, isCollectionType]
  );

  return (
    <div
      className="card"
      style={getStyle}
      key={`${isSelected}_${location.id}_${regionName}`}
    >
      <div className="card-block">
        {location.distance > 0 && (
          <h6 className="card-title text-muted">
            {Math.round(location.distance)}m from a new run location
          </h6>
        )}
        <h4 className="card-title">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onLoadManagedLocation(location.id);
            }}
          >
            {location.locationName}
          </a>
          {location.hasTankDropoffs && (
            <span>
              <i
                className={"fa fa-plus-square text-danger ml-1"}
                data-toggle="tooltip"
                data-placement="top"
                title="Has tanks to drop off"
              />
              <small style={{ fontSize: "45%" }}>&nbsp;Drop-off</small>
            </span>
          )}
          {location.hasTankPickups && (
            <span>
              <i
                className={"fa fa-minus-square text-danger ml-1"}
                data-toggle="tooltip"
                data-placement="top"
                title="Has tanks to pick up"
              />
              <small style={{ fontSize: "45%" }}>&nbsp;Pickup</small>
            </span>
          )}

          {!isSelected && (
            <span className="pull-right">
              <Button
                color="primary"
                onClick={(e) => onToggleRunSearch(e, location)}
              >
                <i className="fa fa-search" />
                &nbsp;&nbsp;Add to existing run
              </Button>
              &nbsp;
              <Button
                color="primary"
                onClick={(e) => dispatch(addLocationToNewRun(location))}
              >
                <i className="fa fa-plus" />
                &nbsp;&nbsp;Add to new run
              </Button>
            </span>
          )}
          {isSelected && (
            <span className="pull-right">
              <Button
                color="primary"
                onClick={(e) => onRemoveLocation(location)}
              >
                <i className="fa fa-minus-square" />
                &nbsp;&nbsp;Remove from new run
              </Button>
            </span>
          )}
        </h4>
        {(location.isInClosedPeriod || location.isClosedOnDeliveryDate) && (
          <div className={"row"}>
            <h5>
              <div
                className="text-muted pull-left"
                style={{ marginLeft: "15px" }}
              >
                <i className={"fa fa-exclamation-triangle fa-2 text-warning"} />
                &nbsp;&nbsp;
                {location.isInClosedPeriod &&
                  location.isClosedOnDeliveryDate &&
                  "Location is currently closed and closed for delivery date"}
                {location.isInClosedPeriod &&
                  !location.isClosedOnDeliveryDate &&
                  "Location is currently closed, however open on delivery date"}
                {!location.isInClosedPeriod &&
                  location.isClosedOnDeliveryDate &&
                  "Location is currently open, however closed on delivery date"}
              </div>
            </h5>
          </div>
        )}
        {location.isNonBulk ? (
          <h5>
            <div>
              <div className="text-muted small">
                {isCollectionType
                    ? "Expected: " + round(location.estimatedToReach, 1)
                    : "Expected to deliver: " + round(location.estimatedToReach, 1)}
                L on{" "}
                {moment(location.estimatedToReachDate).format(
                    SHORT_DATE_FORMAT
                )}
              </div>
              <div className="text-muted small">
                Expected {location.customerTypeName === "Tin" ? "tins" : "BIBs"}
                : {location.tinsExpected}
              </div>
              <div className="text-muted small">
                Expected {location.customerTypeName === "Tin" ? "tins" : "BIBs"}{" "}
                + 7: {location.tinsExpectedPlus7}
              </div>
              <div className="text-muted small">
                Days since last: {location.daysSinceLastPickup}
              </div>
            </div>
          </h5>
        ) : (
          <h5>
            <div className="text-right">
              <div className="text-muted pull-left">
                {isCollectionType
                    ? "Estimated to reach: " + round(location.estimatedToReach, 1)
                    : "Estimated to deliver: " + round(location.estimatedToReach, 1)}
                L on{" "}
                {moment(location.estimatedToReachDate).format(
                  SHORT_DATE_FORMAT
                )}
              </div>
              <div className="text-muted">
                Max capacity: {location.capacityLitres}L
              </div>
            </div>

            <Progress
              style={{ marginTop: "10px", marginBottom: "10px" }}
              max={100}
              value={location.percentageOfCapacity === 0 ? 100 : location.percentageOfCapacity}                
              color={getProgressColor()}              
            >
              { isCollectionType ? <span>Projected {location.percentageOfCapacity}%</span> :
                  <span>Remaining {location.percentageOfCapacity}%</span>
              }
            </Progress>

            <Progress
              style={{ marginTop: "10px", marginBottom: "10px" }}
              max={100}
              value={location.percentageOfCapacityPlus7 === 0 ? 100 : location.percentageOfCapacityPlus7}
              color={getProgressColor(true)}
            >
              { isCollectionType ? <span>Projected + 7 days {location.percentageOfCapacityPlus7}%</span> :
                  <span>Remaining + 7 days {location.percentageOfCapacityPlus7}%</span>
              }              
            </Progress>
            <div className="text-muted small">
              Actual rate: {round(location.actualForPeriod, 2)} L/D
            </div>
            <div className="text-muted small">
              Actual last year: {round(location.actualLastYear, 2)} L/D
            </div>
            <div className="text-muted small">
              Forecast: {round(location.forecastRate, 2)} L/D
            </div>
            <div className="text-muted small">
              Days since last: {location.daysSinceLastPickup} days
            </div>
          </h5>
        )}

        <div className="row">
          <div className="col-md-12 col-xs-12">
            <strong>
              <i className="fa fa-map-pin" />
              &nbsp;&nbsp;
            </strong>
            {location.fullAddress}
          </div>
        </div>
        {location.contacts.map((x, index) => {
          return (
            <div className="row" key={`contact-${x.id}-${index}`}>
              <div className="col-md-12 col-xs-12">
                <strong>
                  <i className="fa fa-male" />
                  &nbsp;&nbsp;
                </strong>
                <span style={{ marginRight: "10px" }}>{x.fullName}</span>
                {x.communications
                  .filter((y) =>
                    y.communicationType.toLowerCase().includes("phone")
                  )
                  .map((c, index) => {
                    return (
                      <a
                        key={`communications-${c.id}-${index}`}
                        href={`tel:${c.detail}`}
                        style={{ marginRight: "10px" }}
                      >
                        <strong>
                          <i className="fa fa-phone" />
                          &nbsp;
                        </strong>
                        {c.detail}
                      </a>
                    );
                  })}
              </div>
            </div>
          );
        })}
        {location.communications
          .filter((y) => y.communicationType.toLowerCase().includes("phone"))
          .map((x, i) => {
            return (
              <div
                className="row"
                key={`locations-communications-${x.id}-${i}`}
              >
                <div className="col-md-12 col-xs-12">
                  <a href={`tel:${x.detail}`}>
                    <strong>
                      <i className="fa fa-phone" />
                      &nbsp;&nbsp;
                    </strong>
                    {x.detail}
                  </a>
                </div>
              </div>
            );
          })}
        {location.nextPickupNote && location.nextPickupNote != "" && (
          <p>
            <i className="fa fa-book" /> {location.nextPickupNote}
          </p>
        )}
        <p>
          { location.isUrgentDelivery ?
              <Badge className={`py-q px-q mr-2`} color={"danger"}>
                Requested Urgent Delivery
              </Badge> :
              <Badge className={`py-q px-q mr-2`} color={"success"}>
                No Requests for Urgent Delivery
              </Badge>
          }
          { location.isUrgentCollection ?
              <Badge className={`py-q px-q`} color={"danger"}>
                Requested Urgent Collection
              </Badge> :
              <Badge className={`py-q px-q`} color={"success"}>
                No Requests for Urgent Collection
              </Badge>
          }
        </p>
        {location.runs.filter((x) => !x.isComplete).length > 0 && (
          <ul style={{ marginBottom: "10px" }} className="list-group col-md-6">
            {location.runs
              .filter((x) => !x.isComplete)
              .map((run, index) => (
                <li
                  className="list-group-item list-group-item-warning"
                  key={`runs-${run.id}-${index}`}
                >
                  <a
                    href=""
                    onClick={(e) => onOpenRunModal(e, run.id, location.id)}
                  >
                    {getRunFlag(run.truckCollectionTypeCode)} - {moment(run.timeStart).format("YYYY-MM-DD")} - #
                    {run.id}
                  </a>
                  <a
                    style={{ marginLeft: "auto" }}
                    href={`${BASE_URL}/runs/search/${run.id}`}
                    target={"_blank"}
                  >
                    Edit&nbsp;&nbsp;
                    <i className="fa fa-external-link" />
                  </a>
                </li>
              ))}
          </ul>
        )}
        <div
          className="collapse"
          id={`location_${location.id}_runs`}
          style={{ marginBottom: "10px" }}
        >
          <ul className="list-group col-md-6">
            {location.runs
              .filter((x) => x.isComplete)
              .map((run) => (
                <li
                  className={
                    run.isComplete
                      ? "list-group-item list-group-item-success"
                      : "list-group-item list-group-item-warning"
                  }
                  key={run.id}
                >
                  <a
                    href=""
                    onClick={(e) => onOpenRunModal(e, run.id, location.id)}
                  >
                    Quick view {moment(run.timeStart).format("YYYY-MM-DD")} - #
                    {run.id}
                  </a>
                  <a
                    style={{ marginLeft: "auto" }}
                    href=""
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch(push(`${BASE_URL}/runs/search/${run.id}`));
                    }}
                  >
                    Edit
                  </a>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default LocationItem;
